/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useEffect } from "react";

import styled from "styled-components";

import { Plate } from "src/classes/plate/Plate";
import { KeyCodes } from "src/components/Hotkeys/hotkeys";
import { ContentEditable } from "src/components/Shared/ContentEditable/ContentEditable";

interface Props {
  plate: Plate;
}

export function PlateTitle({ plate }: Props) {
  const title = plate.observe((state) => state.title);
  const [titleValue, setTitleValue] = useState(title);
  const isPublic = plate.observe((state) => state.isPublic);
  const currentUserIsOwner = plate.snapshot.currentUserIsOwner;
  const justCreated = new Date(plate.snapshot.createdAt).getTime() > Date.now() - 2000;

  useEffect(() => {
    // for when the plate title changes outside of the plate title component
    setTitleValue(title);
  }, [title]);

  const handleBlur = () => {
    if (titleValue && titleValue !== title) {
      plate.changeTitle(titleValue);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<Element>) => {
    // handle the meta+a key combination while maintaining the default Enter key behavior
    switch (e.key) {
      case KeyCodes.A:
        if (e.metaKey) {
          e.stopPropagation();
          // select all the text in the title
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(e.target as Node);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        break;
      case KeyCodes.ENTER:
        e.preventDefault();
        if (e.target) {
          (e.target as HTMLElement).blur();
        }
        break;
      // allow arrow keys to move the cursor
      case KeyCodes.ARROW_LEFT:
      case KeyCodes.ARROW_RIGHT:
      case KeyCodes.ARROW_UP:
      case KeyCodes.ARROW_DOWN:
        e.stopPropagation();
        break;
      default:
        break;
    }
  };

  return (
    <StyledTitle
      editable={currentUserIsOwner}
      value={title}
      onChange={setTitleValue}
      autoFocus={currentUserIsOwner && justCreated}
      onBlur={handleBlur}
      isPublic={isPublic}
      isOwner={currentUserIsOwner}
      onKeyDownOverride={onKeyDown}
    />
  );
}

const StyledTitle = styled(ContentEditable)<{ isPublic: boolean; isOwner: boolean }>`
  flex-shrink: 0;
  margin-right: 5px;
  outline: none;
  border: 1px solid transparent;
  padding: 3px;
  color: ${({ theme, isPublic }) => (isPublic ? "inherit" : theme.accent)};
  cursor: ${({ isOwner }) => (isOwner ? "text" : "default")};
  &:focus {
    border: ${({ theme }) => `1px solid ${theme.primary}`};
  }
`;

export const ReadOnlyTitle = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text.default};
  flex-shrink: 0;
  margin-right: 5px;
  margin-left: 60px;
  outline: none;
  border: 1px solid transparent;
  padding: 3px;
  cursor: default;
`;
